import { useEffect, useState } from 'react'
import {
  Container, Tabs, Title, Text, Stack, Group, Avatar,
  TextInput, Badge, Divider, Button, Paper, Loader, Center, Table, ScrollArea,
} from '@mantine/core'
import { useNavigate } from 'react-router-dom'
import { User, CreditCard, Activity, ClipboardList } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import api from '../../api/client'
import { evaluationsApi, StudentEvaluationHistoryResponse } from '../../api/evaluations'

interface FullUser {
  id: number
  email: string
  user_name: string
  first_name?: string
  last_name?: string
  role: string
  is_active: boolean
  subscription_status: string
  plan_code: string
  subscription_end_date?: string | null
}

function formatDate(value?: string | null): string {
  return value ? new Date(value).toLocaleDateString() : '—'
}

function planLabel(code: string): string {
  const map: Record<string, string> = {
    lifetime: 'Lifetime',
    trial: 'Trial',
    monthly: 'Monthly',
    '3month': '3-Month',
    '6month': '6-Month',
    annual: 'Annual',
  }
  return map[code] ?? code
}

function planColor(status: string): string {
  if (status === 'active') return 'teal'
  if (status === 'trial') return 'blue'
  return 'gray'
}

export function Profile() {
  const { user: ctxUser } = useAuth()
  const navigate = useNavigate()
  const [profile, setProfile] = useState<FullUser | null>(null)
  const [history, setHistory] = useState<StudentEvaluationHistoryResponse | null>(null)
  const [historyLoading, setHistoryLoading] = useState(true)
  const activeRole = ctxUser?.role ?? profile?.role

  useEffect(() => {
    api.get<FullUser>('/auth/me').then((res) => setProfile(res.data))
  }, [])

  useEffect(() => {
    if (!activeRole) {
      return
    }

    if (activeRole !== 'student') {
      setHistory(null)
      setHistoryLoading(false)
      return
    }

    setHistoryLoading(true)
    evaluationsApi.history()
      .then((res) => setHistory(res.data))
      .catch(() => setHistory({ progress: [], practice_records: [] }))
      .finally(() => setHistoryLoading(false))
  }, [activeRole])

  if (!profile) {
    return (
      <Center h={200}>
        <Loader color="grapefruit" />
      </Center>
    )
  }

  const isLifetime = profile.plan_code === 'lifetime'
  const isStudent = profile.role === 'student'
  const endDate = profile.subscription_end_date
    ? new Date(profile.subscription_end_date).toLocaleDateString(undefined, {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    : null

  return (
    <Container size="sm" py="xl">
      <Group mb="xl" gap="md">
        <Avatar src={ctxUser?.avatar} radius="xl" size="lg" color="grapefruit">
          {profile.user_name?.[0]?.toUpperCase()}
        </Avatar>
        <div>
          <Title order={3} style={{ color: 'var(--sat-text)' }}>{profile.user_name}</Title>
          <Text size="sm" c="dimmed">{profile.email}</Text>
        </div>
      </Group>

      <Tabs defaultValue="account" color="grapefruit">
        <Tabs.List mb="lg">
          <Tabs.Tab value="account" leftSection={<User size={14} />}>Account</Tabs.Tab>
          <Tabs.Tab value="plan" leftSection={<CreditCard size={14} />}>Plan</Tabs.Tab>
          {isStudent && <Tabs.Tab value="progress" leftSection={<Activity size={14} />}>Progress</Tabs.Tab>}
          {isStudent && <Tabs.Tab value="practice-record" leftSection={<ClipboardList size={14} />}>Practice Record</Tabs.Tab>}
        </Tabs.List>

        <Tabs.Panel value="account">
          <Paper withBorder p="lg" radius="md" bg="var(--sat-bg)">
            <Stack gap="md">
              <TextInput label="Username" value={profile.user_name} readOnly />
              <TextInput label="Email" value={profile.email} readOnly />
              {(profile.first_name || profile.last_name) && (
                <TextInput
                  label="Full Name"
                  value={`${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim()}
                  readOnly
                />
              )}
              <Divider />
              <div>
                <Text size="sm" fw={500} mb={6}>Password</Text>
                <Text size="sm" c="dimmed" mb={8}>
                  To reset your password, we'll send a reset link to your email.
                </Text>
                <Button
                  variant="outline"
                  color="grapefruit"
                  size="sm"
                  onClick={() => navigate('/forgot-password')}
                >
                  Reset Password
                </Button>
              </div>
            </Stack>
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="plan">
          <Paper withBorder p="lg" radius="md" bg="var(--sat-bg)">
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={600}>Current Plan</Text>
                <Badge color={planColor(profile.subscription_status)} size="lg">
                  {planLabel(profile.plan_code)}
                </Badge>
              </Group>

              <Group justify="space-between">
                <Text fw={600}>Status</Text>
                <Badge color={planColor(profile.subscription_status)} variant="light">
                  {profile.subscription_status}
                </Badge>
              </Group>

              {!isLifetime && (
                <Group justify="space-between">
                  <Text fw={600}>Expires</Text>
                  <Text c={endDate ? undefined : 'dimmed'}>
                    {endDate ?? '—'}
                  </Text>
                </Group>
              )}

              {isLifetime && (
                <Group justify="space-between">
                  <Text fw={600}>Expires</Text>
                  <Badge color="teal" variant="light">Lifetime Access</Badge>
                </Group>
              )}

              <Divider />
              <Button
                variant="outline"
                color="grapefruit"
                onClick={() => navigate('/subscription')}
              >
                Manage Subscription
              </Button>
            </Stack>
          </Paper>
        </Tabs.Panel>

        {isStudent && (
          <Tabs.Panel value="progress">
            <Paper withBorder p="lg" radius="md" bg="var(--sat-bg)">
              {historyLoading ? (
                <Center h={160}>
                  <Loader color="grapefruit" />
                </Center>
              ) : history?.progress.length ? (
                <ScrollArea>
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Date</Table.Th>
                        <Table.Th>R&W</Table.Th>
                        <Table.Th>Math</Table.Th>
                        <Table.Th>R&W Hard Module Unlocked</Table.Th>
                        <Table.Th>Math Hard Module Unlocked</Table.Th>
                        <Table.Th>Total Score</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {history.progress.map((item) => (
                        <Table.Tr key={item.id}>
                          <Table.Td>{formatDate(item.create_datetime)}</Table.Td>
                          <Table.Td>{item.rw_score}</Table.Td>
                          <Table.Td>{item.math_score}</Table.Td>
                          <Table.Td>
                            <Badge color={item.rw_hard_flag ? 'teal' : 'gray'} variant="light">
                              {item.rw_hard_flag ? 'Yes' : 'No'}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Badge color={item.math_hard_flag ? 'teal' : 'gray'} variant="light">
                              {item.math_hard_flag ? 'Yes' : 'No'}
                            </Badge>
                          </Table.Td>
                          <Table.Td>{item.total_score}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              ) : (
                <Text c="dimmed">No evaluation progress history yet.</Text>
              )}
            </Paper>
          </Tabs.Panel>
        )}

        {isStudent && (
          <Tabs.Panel value="practice-record">
            <Paper withBorder p="lg" radius="md" bg="var(--sat-bg)">
              {historyLoading ? (
                <Center h={160}>
                  <Loader color="grapefruit" />
                </Center>
              ) : history?.practice_records.length ? (
                <ScrollArea>
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Date</Table.Th>
                        <Table.Th>Title</Table.Th>
                        <Table.Th>R&W Right Answer</Table.Th>
                        <Table.Th>Math Correct Answer</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {history.practice_records.map((item) => (
                        <Table.Tr key={item.id}>
                          <Table.Td>{formatDate(item.create_datetime)}</Table.Td>
                          <Table.Td>{item.title}</Table.Td>
                          <Table.Td>{item.rw_score}</Table.Td>
                          <Table.Td>{item.math_score}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              ) : (
                <Text c="dimmed">No practice records yet.</Text>
              )}
            </Paper>
          </Tabs.Panel>
        )}
      </Tabs>
    </Container>
  )
}
